import type { SheetDataStore } from "@/routes/smart-table/stores/sheet-data-store";

const MAX_ROWS_TO_SCAN = 100;
const TOOL_PREFIX = "Tool: ";
const TOOL_EXECUTION_NODE_TYPE = "TOOL_EXECUTION";

const parseMaybeJson = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const unwrapCellValue = (value: unknown): unknown => {
  let current = parseMaybeJson(value);
  if (current && typeof current === "object" && !Array.isArray(current) && "value" in current) {
    current = parseMaybeJson((current as { value: unknown }).value);
  }
  return current;
};

const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed || null;
};

const getMappingValue = (mapping: unknown, key: string): unknown => {
  if (!mapping || typeof mapping !== "object" || Array.isArray(mapping)) {
    return undefined;
  }
  const record = mapping as Record<string, unknown>;
  if (key in record) {
    return record[key];
  }
  let current: unknown = record;
  for (const part of key.split(".")) {
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

const spanAttributeMappings = (span: Record<string, unknown>): Record<string, unknown>[] => {
  const mappings: Record<string, unknown>[] = [];
  for (const key of ["metadata", "attributes"] as const) {
    const value = span[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      mappings.push(value as Record<string, unknown>);
    }
  }
  return mappings;
};

const nodeType = (span: Record<string, unknown>): string | null => {
  for (const mapping of spanAttributeMappings(span)) {
    const value = asNonEmptyString(getMappingValue(mapping, "node_type"));
    if (value) {
      return value;
    }
  }
  return null;
};

const toolNameAttr = (span: Record<string, unknown>): string | null => {
  for (const mapping of spanAttributeMappings(span)) {
    const value = asNonEmptyString(getMappingValue(mapping, "tool.name"));
    if (value) {
      return value;
    }
  }
  return null;
};

const toolNameFromSpan = (span: Record<string, unknown>): string | null => {
  const { name } = span;
  if (typeof name === "string" && name.startsWith(TOOL_PREFIX)) {
    return asNonEmptyString(name.slice(TOOL_PREFIX.length));
  }

  if (nodeType(span) !== TOOL_EXECUTION_NODE_TYPE) {
    return null;
  }

  const fromAttr = toolNameAttr(span);
  if (fromAttr) {
    return fromAttr;
  }

  return typeof name === "string" ? asNonEmptyString(name) : null;
};

export const extractTrajectoryToolNames = (trace: unknown): string[] => {
  const tools = new Set<string>();

  const walk = (node: unknown) => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    const record = node as Record<string, unknown>;
    const toolName = toolNameFromSpan(record);
    if (toolName) {
      tools.add(toolName);
    }
    walk(record.children);
  };

  walk(unwrapCellValue(trace));
  return Array.from(tools);
};

export const collectToolNamesFromColumn = (
  store: SheetDataStore | null | undefined,
  columnId: string,
): string[] => {
  if (!store || !columnId) {
    return [];
  }

  const tools = new Set<string>();
  let scanned = 0;
  for (const cellMap of store.rows.values()) {
    if (scanned >= MAX_ROWS_TO_SCAN) {
      break;
    }
    scanned += 1;
    const cell = cellMap.get(columnId);
    if (!cell) {
      continue;
    }
    extractTrajectoryToolNames(cell.value ?? cell.display_value).forEach(tool => {
      tools.add(tool);
    });
  }

  return Array.from(tools).sort((left, right) => left.localeCompare(right));
};
